import React, { useState, useRef, useEffect } from 'react';
import { ExpandMoreIcon } from '../../components/Icons';
import useStyles from '../maintenance/assets/styles';

interface SearchableSelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function SearchableSelect({ options, value, onChange, placeholder = 'Select...', disabled = false }: SearchableSelectProps) {
  const style = useStyles();
  const [search, setSearch] = useState(value);
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearch(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = () => {
    if (disabled) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(true);
  };

  const filtered = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

  const handleSelect = (opt: string) => {
    onChange(opt);
    setSearch(opt);
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!open) openDropdown();
    if (e.target.value === '') onChange('');
  };

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
    } else {
      setSearch('');
      openDropdown();
    }
  };

  return (
    <div ref={containerRef} className={style.ssContainer}>
      <div className={style.ssInputWrapper}>
        <input
          className={`form-input selector ${style.ssInput}`}
          value={search}
          onChange={handleInputChange}
          onFocus={openDropdown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        <span
          className={`${style.ssToggle} ${disabled ? style.ssToggleDisabled : ''}`}
          onClick={toggleOpen}
        >
          <span className={`${style.ssIcon} ${open ? style.ssIconOpen : ''}`}>
            <ExpandMoreIcon />
          </span>
        </span>
      </div>

      {open && !disabled && (
        <div
          className={style.ssDropdown}
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          {filtered.length > 0 ? filtered.map((opt, i) => (
            <div
              key={i}
              className={`${style.ssOption} ${opt === value ? style.ssOptionSelected : ''}`}
              onMouseDown={() => handleSelect(opt)}
            >
              {opt}
            </div>
          )) : (
            <div className={style.ssNoResults}>No results</div>
          )}
        </div>
      )}
    </div>
  );
}
